import React, { useEffect, useState } from "react";
import { Badge, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import HttpManager from "../../../../services/HttpManager";
import { formatLocalDateTime } from "../../../../components/enrollment/format";
import { PaymentIntentDetailsDTO, PAYMENT_INTENT_STATUS } from "../../../../models/EnrollmentHistoryDTO";
import { errorText, HORARIO_LISTA, intentStatusVariant } from "../../managment/enrollmentRequests/helpers";
import { abrirArchivo } from "../../../../components/enrollment/visorArchivo";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { horarioBonito } from "../../../../utils/horarioBonito";
import useEsTelefono from "../../../../utils/esTelefono";
import AccionesAlumno from "../../../../components/enrollment/accionesAlumno";
import { AdjuntosInput, AdjuntosVista, capturasDelPortapapeles } from "../../../../components/enrollment/adjuntos";
import { EtiquetaAlumno } from "./delAlumno";
import RegisterPaymentModal, { serverError } from "../../../../components/modals/payments/register/registerPaymentModal";
import { tituloEstado } from "../../../../utils/tituloEstado";

export interface PaymentIntentDetailsModalProps {
	campusId: number;
	countryId: number;
	intentId: number;
	onClose: () => void;
	onChanged: () => void;
}

const T = {
	title: "Ticket",
	student: "Alumno",
	enrollment: "Matrícula",
	reportedBy: "Reportó",
	receipts: "Comprobantes",
	notes: "Nota del Pago",
	resolutionNotes: "Nota de Caja (opcional)",
	process: "Marcar como Procesado",
	reject: "Rechazar",
	rejectPrompt: "Motivo del Rechazo",
	whichReceipt: "¿Qué comprobante rechazas?",
	pickReceipt: "Marca el comprobante que rechazas.",
	rejectedTag: "Rechazado",
	history: "Historial del Ticket",
	historyTypes: { creado: "Ticket creado", creadoAlumno: "El alumno reportó su pago", rechazado: "Caja rechazó", reenviado: "Ventas mandó comprobante nuevo", procesado: "Caja lo procesó", aplicado: "Caja registró el pago", reabierto: "Se anuló el pago: vuelve a Por Procesar", movimientoAnulado: "Se anuló un movimiento del pago" } as Record<string, string>,
	register: "Registrar Pago",
	pickPayment: "¿Qué pago de WAPP corresponde a este comprobante?",
	noCandidates: "Este alumno no tiene pagos de los últimos 90 días sin ticket. Regístralo con \"Registrar Pago\".",
	confirmLink: "Marcar Procesado con Este Pago",
	processed: "Listo: el ticket quedó cerrado y ligado a su pago.",
	rejected: "Ticket rechazado.",
	confirm: "Rechazar",
	back: "Atrás",
	resolution: "Resolución",
	confirmProcess: "¿Marcar este ticket como procesado?"
};

/* En el teléfono la etiqueta va arriba del dato, como en la solicitud: a dos columnas el dato quedaba de 170px. */
const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
	const movil = useEsTelefono();
	return (
		<div style={{ display: "flex", flexDirection: movil ? "column" : "row", gap: movil ? 0 : 8, padding: movil ? "5px 0" : "2px 0", borderBottom: "1px dotted var(--wa-filo)" }}>
			<div style={{ width: movil ? "auto" : 150, color: "var(--wa-apagado)", flexShrink: 0, fontSize: movil ? 13 : undefined, textTransform: movil ? "uppercase" : undefined }}>{label}</div>
			<div style={{ flex: 1 }}>{children}</div>
		</div>
	);
};

/**
 * Lo que ve Caja de una matrícula nueva ya aprobada: el comprobante, el monto y la nota.
 * Por ahora solo se marca como procesado, como se cierra un ticket; el pago se registra
 * en Pagos como siempre. Más adelante se aplicará desde aquí (Esteban, 2026-09-22).
 */
const PaymentIntentDetailsModal: React.FC<PaymentIntentDetailsModalProps> = ({ campusId, intentId, onClose, onChanged }) => {
	const http = HttpManager.getInstance();
	const movil = useEsTelefono();
	const [d, setD] = useState<PaymentIntentDetailsDTO | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [resolutionNotes, setResolutionNotes] = useState<string>("");
	const [rejecting, setRejecting] = useState<boolean>(false);
	const [rejectText, setRejectText] = useState<string>("");
	const [rejectIdx, setRejectIdx] = useState<number[]>([]);
	const [rejectFiles, setRejectFiles] = useState<File[]>([]);
	const [registering, setRegistering] = useState<boolean>(false);
	const [linking, setLinking] = useState<boolean>(false);
	const [candidates, setCandidates] = useState<any[] | null>(null);
	const [paymentId, setPaymentId] = useState<number>(0);

	const load = () => http.paymentIntentsService.details(campusId, intentId)
		.then((r) => setD(r.data))
		.catch((e) => setError(errorText(e)));
	useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [campusId, intentId]);

	/*
		"Marcar como procesado" (Cobros unificado, 2026-10-06): obliga a ELEGIR el pago de WAPP que corresponde a este
		comprobante; nunca solo de palabra. Se liga al ticket y no puede estar ligado a otro.
	*/
	const startLinking = async () => {
		setLinking(true);
		setPaymentId(0);
		setCandidates(null);
		try {
			const r = await http.paymentIntentsService.candidates(campusId, intentId);
			setCandidates(r.data || []);
		} catch (e) {
			setError(serverError(e));
			setCandidates([]);
		}
	};

	// Sin await antes de abrirArchivo: la pestaña se abre en el mismo clic (en celular se bloqueaba).
	const viewDocument = (index?: number, nombre?: string) =>
		abrirArchivo({ origen: "Comprobantes de pago", nombre: nombre || "Comprobante", cargar: () => http.paymentIntentsService.downloadDocument(campusId, intentId, index).then((r) => r.data) })
			.catch((e) => setError(errorText(e)));

	const process = async () => {
		if (!paymentId) {
			return;
		}
		setBusy(true);
		setError(null);
		try {
			const r = await http.paymentIntentsService.process(campusId, intentId, resolutionNotes.trim(), paymentId);
			setD(r.data);
			setLinking(false);
			setNotice(T.processed);
			onChanged();
		} catch (e) {
			setError(serverError(e));
		} finally {
			setBusy(false);
		}
	};

	const reject = async () => {
		if (rejectText.trim() === "") {
			setError("Escribe el motivo del rechazo.");
			return;
		}
		if (rejectIdx.length < 1) {
			setError(T.pickReceipt);
			return;
		}
		setBusy(true);
		setError(null);
		try {
			const r = await http.paymentIntentsService.reject(campusId, intentId, rejectText.trim(), rejectIdx, rejectFiles);
			setD(r.data);
			setRejecting(false);
			setNotice(T.rejected);
			onChanged();
		} catch (e) {
			setError(errorText(e));
		} finally {
			setBusy(false);
		}
	};

	const documents = d?.documents && d.documents.length > 0
		? d.documents
		: (d?.hasDocument ? [{ index: 0, name: d.documentName || "Comprobante" }] : []);
	const vigentes = documents.filter((doc: any) => !doc.rejected);
	const empezarRechazo = () => {
		setRejecting(true);
		setRejectText("");
		setRejectFiles([]);
		//Con un solo comprobante vigente ya va marcado.
		setRejectIdx(vigentes.length === 1 ? [vigentes[0].index] : []);
	};

	const ancho = movil ? { flex: "1 1 0", padding: "10px 8px", fontSize: 15 } : undefined;
	const tam = movil ? "" : " btn-sm";

	return (
		<Modal className="waModulo" show onHide={onClose} size="lg" centered scrollable fullscreen="sm-down" enforceFocus={false}>
			<Modal.Header closeButton>
				<Modal.Title style={{ fontSize: 18 }}>
					{T.title} #{intentId}
					{d ? <Badge bg={intentStatusVariant(d.statusId)} style={{ marginLeft: 10, fontWeight: 500 }}>{tituloEstado(d.status)}</Badge> : ""}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ textAlign: "left", fontSize: 15 }}>
				{error ? <div className="alert alert-danger" style={{ fontSize: 15 }}>{error}</div> : ""}
				{notice ? <div className="alert alert-success" style={{ fontSize: 15 }}>{notice}</div> : ""}
				{!d ? <div className="loader" /> : (
					<>
						{d.fromStudent ? (
							/* "Reporta tu pago" (/pagar, 2026-10-10): lo que escribió el alumno; Caja aplica el pago en Finanzas. */
							<>
								<Row label={T.student}>
									{d.studentId
										? <><b>{d.studentName}</b> <span style={{ color: "var(--wa-apagado)" }}>· {d.identityCode || "-"}</span></>
										: <><b>{d.contactName || "-"}</b> <span style={{ color: "var(--wa-oro)" }}>· Sin alumno en WAPP: búscalo por nombre o teléfono</span></>}
									<div style={{ marginTop: 2 }}><EtiquetaAlumno /></div>
								</Row>
								{d.studentId && d.contactName && d.contactName !== d.studentName ? <Row label="Escribió">{d.contactName}</Row> : ""}
								{/* Con lada y solo dígitos, como Woztell (Esteban): 504 + 8 en Honduras, 521 + 10 en México. */}
								<Row label="Teléfono">{d.contactPhone || "-"}</Row>
								<Row label="Correo">{d.contactEmail || "-"}</Row>
								{d.contactCountry ? <Row label="País">{d.contactCountry}</Row> : ""}
								<Row label="Motivo">{d.notes ? <span style={{ whiteSpace: "pre-wrap" }}>{d.notes}</span> : "-"}</Row>
							</>
						) : <Row label={T.student}><b>{d.studentName}</b> <span style={{ color: "var(--wa-apagado)" }}>· {d.identityCode || "-"}</span></Row>}
						{d.fromStudent ? "" : d.kind === "kit"
							? <Row label="Producto">{d.concept}{d.articles && d.articles.length ? <div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>{d.articles.map((a) => a.name + ": pendiente " + a.pending.toLocaleString("es-MX", { minimumFractionDigits: 2 })).join(" · ")}</div> : null}</Row>
							: <Row label={T.enrollment}>{cursoCorto(d.courseName)} · {horarioBonito(d.scheduleName, HORARIO_LISTA)}</Row>}
						{d.fromStudent ? "" : <Row label={T.notes}><span style={{ whiteSpace: "pre-wrap" }}>{d.notes || "-"}</span></Row>}
						<Row label={T.receipts}>
							{documents.length < 1 ? "-" : documents.map((doc: any) => (
								<div key={"doc_" + doc.index} style={{ padding: "2px 0" }}>
									{rejecting && !doc.rejected ? (
										<input type="checkbox" style={{ marginRight: 8 }} checked={rejectIdx.includes(doc.index)} disabled={busy}
											onChange={() => setRejectIdx((l) => l.includes(doc.index) ? l.filter((x) => x !== doc.index) : [...l, doc.index])} />
									) : ""}
									<button type="button" className="btn btn-link btn-sm" style={{ padding: 0, textDecoration: doc.rejected ? "line-through" : undefined }} onClick={() => viewDocument(doc.index, doc.name)}>
										<FontAwesomeIcon icon={solid("paperclip")} />&nbsp;{doc.name}
									</button>
									{doc.rejected ? <Badge bg="oro" style={{ marginLeft: 8, fontWeight: 500 }}>{T.rejectedTag}</Badge> : ""}
									{doc.rejected ? <div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>{doc.rejectedBy} · {formatLocalDateTime(doc.rejectedAt)}: {doc.rejectionReason}</div> : ""}
									{doc.comment ? <div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>{doc.uploadedBy} · {formatLocalDateTime(doc.uploadedAt)}: {doc.comment}</div> : ""}
								</div>
							))}
						</Row>
						{/* "Reportó" repetía a Admisiones (el ticket nace al aprobar): queda solo la fecha (Esteban, 2026-09-25). */}
						<Row label="Creado">{formatLocalDateTime(d.createdAt)}</Row>
						{/* A quién le tocó por turno; cualquiera de Caja del plantel lo puede trabajar (2026-09-25). */}
						{d.salesShort ? <Row label="Envió">{d.salesShort}</Row> : ""}
						{d.admissionsShort || d.createdByShort ? <Row label="Admisiones">{d.admissionsShort || d.createdByShort}</Row> : ""}
						{d.assignedToShort ? <Row label="Cajera">{d.assignedToShort}</Row> : ""}
						{/* Con historial, la resolución ya está ahí; solo los tickets viejos la muestran aparte. */}
						{d.statusId !== PAYMENT_INTENT_STATUS.PENDIENTE && (d.history || []).length < 1 ? (
							<Row label={T.resolution}>
								{d.resolvedBy || "-"} <span style={{ color: "var(--wa-apagado)" }}>· {formatLocalDateTime(d.resolvedAt)}</span>
								{d.resolutionNotes ? <div style={{ whiteSpace: "pre-wrap" }}>{d.resolutionNotes}</div> : ""}
							</Row>
						) : ""}

						{(d.history || []).length > 0 ? (
							<>
								<div style={{ fontWeight: 700, margin: "14px 0 4px" }}>{T.history}</div>
								{[...(d.history || [])].map((e, k) => ({ e, k })).reverse().map(({ e, k }) => (
									<div key={"h_" + k} style={{ padding: "4px 0", borderBottom: "1px dotted var(--wa-filo)" }}>
										<b style={{ fontSize: 14 }}>{(e.type === "creado" && d.fromStudent ? T.historyTypes.creadoAlumno : T.historyTypes[e.type]) || e.type}</b>
										{e.docs && e.docs.length > 0 ? <span style={{ fontSize: 14 }}> · {e.docs.join(", ")}</span> : ""}
										{e.text ? <div style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>{e.text}</div> : ""}
										<AdjuntosVista adjuntos={e.attachments} cargar={(i) => http.paymentIntentsService.historyAttachment(campusId, intentId, k, i)} />
										<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>{e.by || "-"} · {formatLocalDateTime(e.at)}</div>
									</div>
								))}
							</>
						) : ""}

						{rejecting ? (
							<div className="waRechazo" style={{ marginTop: 12 }}>
								{vigentes.length > 1 ? <div style={{ fontWeight: 600, marginBottom: 6 }}>{T.whichReceipt} <span style={{ fontWeight: 400, color: "var(--wa-apagado)" }}>(márcalo arriba)</span></div> : ""}
								<label style={{ position: "static", fontSize: 14, color: "var(--wa-apagado)", display: "block", marginBottom: 3 }}>{T.rejectPrompt} *</label>
								<textarea className="form-control" rows={3} autoFocus value={rejectText} disabled={busy} onChange={(e) => setRejectText(e.target.value)}
									onPaste={(e) => { const c = capturasDelPortapapeles(e); if (c.length) { e.preventDefault(); setRejectFiles([...rejectFiles, ...c].slice(0, 5)); } }} />
								<div style={{ marginTop: 8 }}><AdjuntosInput files={rejectFiles} onChange={setRejectFiles} onError={setError} disabled={busy} /></div>
							</div>
						) : (d.permissions.canProcess && linking ? (
							<div style={{ marginTop: 12, border: "1px solid var(--wa-filo)", padding: 10 }}>
								<div style={{ fontWeight: 700, marginBottom: 6 }}>{T.pickPayment}</div>
								{candidates === null ? <div className="loader" /> : candidates.length < 1 ? <div style={{ color: "var(--wa-oro)", fontWeight: 600 }}>{T.noCandidates}</div> : (
									<div style={{ display: "grid", gap: 4 }}>
										{candidates.map((c: any) => (
											<label key={"c" + c.id} style={{ position: "static", display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 8px", cursor: "pointer",
												border: "1px solid " + (paymentId === c.id ? "var(--wa-acento)" : "var(--wa-filo)"), background: paymentId === c.id ? "var(--wa-acento-suave)" : undefined }}>
												<input type="radio" name="pagoDelTicket" checked={paymentId === c.id} disabled={busy} onChange={() => setPaymentId(c.id)} style={{ marginTop: 4 }} />
												<span style={{ fontSize: 14 }}>
													<b>{c.label}</b> · {c.paymentDate ? c.paymentDate.slice(8, 10) + "/" + c.paymentDate.slice(5, 7) + "/" + c.paymentDate.slice(0, 4) : ""} · <b>{Number(c.amount).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</b>
													<span style={{ color: "var(--wa-apagado)" }}>{c.reference ? " · ref. " + c.reference : ""} · {c.method} · {c.account} · factura {c.invoiceId}</span>
												</span>
											</label>
										))}
									</div>
								)}
								<label style={{ position: "static", fontSize: 13, color: "var(--wa-apagado)", marginTop: 8, display: "block" }}>{T.resolutionNotes}</label>
								<textarea className="form-control" rows={2} value={resolutionNotes} disabled={busy} onChange={(e) => setResolutionNotes(e.target.value)} />
							</div>
						) : "")}
					</>
				)}
			</Modal.Body>
			<Modal.Footer style={movil ? { flexDirection: "column", flexWrap: "nowrap", alignItems: "stretch", gap: 8 } : undefined}>
				{rejecting ? (
					<div style={{ display: "flex", gap: 8, justifyContent: "flex-end", width: movil ? "100%" : undefined }}>
						<button type="button" className={"btn btn-outline-secondary" + tam} style={ancho} disabled={busy} onClick={() => setRejecting(false)}>{T.back}</button>
						<button type="button" className={"btn btn-warning" + tam} style={ancho} disabled={busy || rejectText.trim() === "" || rejectIdx.length < 1} onClick={reject}>{T.confirm}</button>
					</div>
				) : movil ? (
					/*
						En el teléfono: perfil y finanzas en un renglón, las dos decisiones en otro;
						cerrar ya está en la X (Esteban, 2026-09-23).
					*/
					<>
						{d && d.studentId ? <AccionesAlumno studentId={d.studentId} campusId={campusId} alinear="inicio" /> : ""}
						{d && d.permissions.canProcess ? (
							linking ? (
								<div style={{ display: "flex", gap: 8 }}>
									<button type="button" className="btn btn-outline-secondary" style={ancho} disabled={busy} onClick={() => setLinking(false)}>{T.back}</button>
									<button type="button" className="btn btn-primary" style={{ ...ancho, flex: "2 1 0" }} disabled={busy || !paymentId} onClick={process}>{T.confirmLink}</button>
								</div>
							) : (
								<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
									<button type="button" className="btn btn-warning" style={ancho} disabled={busy} onClick={empezarRechazo}>{T.reject}</button>
									<button type="button" className="btn btn-outline-primary" style={ancho} disabled={busy} onClick={startLinking}>{T.process}</button>
									<button type="button" className="btn btn-primary" style={{ ...ancho, flex: "2 1 100%" }} disabled={busy} onClick={() => setRegistering(true)}>{T.register}</button>
								</div>
							)
						) : ""}
					</>
				) : (
					<>
						{d && d.studentId ? <div style={{ marginRight: "auto" }}><AccionesAlumno studentId={d.studentId} campusId={campusId} alinear="inicio" /></div> : ""}
						{d && d.permissions.canProcess ? (
							linking ? (
								<>
									<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setLinking(false)}>{T.back}</button>
									<button type="button" className="btn btn-primary btn-sm" disabled={busy || !paymentId} onClick={process}>
										<FontAwesomeIcon icon={solid("check")} />&nbsp;{T.confirmLink}
									</button>
								</>
							) : (
								<>
									<button type="button" className="btn btn-warning btn-sm" disabled={busy} onClick={empezarRechazo}>{T.reject}</button>
									<button type="button" className="btn btn-outline-primary btn-sm" disabled={busy} onClick={startLinking}>{T.process}</button>
									<button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => setRegistering(true)}>{T.register}</button>
								</>
							)
						) : ""}
					</>
				)}
			</Modal.Footer>
			{/* Registrar pago ENCIMA del ticket, en la misma ventana (nunca saltar a Finanzas ni a otra pestaña). */}
			{registering && d ? (
				<RegisterPaymentModal campusId={campusId} currencySymbol={(d as any).currencySymbol || ""} ticket={d}
					onClose={() => { setRegistering(false); load(); }}
					onSuccess={() => { setRegistering(false); setNotice(T.processed); load(); onChanged(); }} />
			) : null}
		</Modal>
	);
};

export default PaymentIntentDetailsModal;
